import React, { useEffect, useState } from "react";
import "@/App.css";
import "@/i18n";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";
import { TopNav } from "@/components/layout/TopNav";
import { Footer } from "@/components/layout/Footer";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { CartDrawer } from "@/components/layout/CartDrawer";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider, useCart } from "@/context/CartContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import Preview from "@/pages/Preview";
import HomeA from "@/pages/HomeA";
import HomeB from "@/pages/HomeB";
import HomeC from "@/pages/HomeC";
import Login from "@/pages/Login";
import StyleGuide from "@/pages/StyleGuide";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NotFound from "@/pages/NotFound";
import AuthGoogle from "@/pages/AuthGoogle";

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            // Defer until the new route has rendered the target anchor
            const id = hash.replace(/^#/, "");
            const tryScroll = () => {
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    return true;
                }
                return false;
            };
            if (!tryScroll()) {
                // Retry once on the next paint for slow-mounting sections
                requestAnimationFrame(() => {
                    if (!tryScroll()) setTimeout(tryScroll, 120);
                });
            }
        } else {
            window.scrollTo({ top: 0, behavior: "instant" });
        }
    }, [pathname, hash]);
    return null;
}

function Shell() {
    const [cmdOpen, setCmdOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const { add, count } = useCart();

    return (
        <>
            <TopNav
                onCommandOpen={() => setCmdOpen(true)}
                onCartOpen={() => setCartOpen(true)}
                cartCount={count}
            />
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<Navigate to="/preview" replace />} />
                <Route path="/preview" element={<Preview />} />
                <Route path="/home-a" element={<HomeA onAddToCart={add} />} />
                <Route path="/home-b" element={<HomeB onAddToCart={add} />} />
                <Route path="/home-c" element={<HomeC onAddToCart={add} />} />
                <Route path="/login" element={<Login />} />
                <Route path="/style-guide" element={<StyleGuide />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/auth/google" element={<AuthGoogle />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
            <Footer />
            <CommandPalette
                open={cmdOpen}
                onOpenChange={setCmdOpen}
                onCartOpen={() => setCartOpen(true)}
            />
            <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
            <Toaster
                position="bottom-right"
                toastOptions={{
                    classNames: {
                        toast: "rounded-card border-2 border-ink-200 bg-white text-ink-800 shadow-lift",
                    },
                }}
            />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <CartProvider>
                        <Shell />
                    </CartProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
